import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
}

export function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    className,
}: PaginationProps) {
    if (totalPages <= 1) return null;

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages: (number | 'ellipsis')[] = [];
        const showEllipsisStart = currentPage > 3;
        const showEllipsisEnd = currentPage < totalPages - 2;

        // Always show first page
        pages.push(1);

        if (showEllipsisStart) {
            pages.push('ellipsis');
        }

        // Show pages around current page
        for (
            let i = Math.max(2, currentPage - 1);
            i <= Math.min(totalPages - 1, currentPage + 1);
            i++
        ) {
            if (!pages.includes(i)) {
                pages.push(i);
            }
        }

        if (showEllipsisEnd) {
            pages.push('ellipsis');
        }

        // Always show last page
        if (totalPages > 1 && !pages.includes(totalPages)) {
            pages.push(totalPages);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <nav
            className={cn('flex items-center justify-center gap-1', className)}
            aria-label="Pagination"
        >
            {/* Previous Button */}
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={cn(
                    'flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-all',
                    currentPage === 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-secondary-700 hover:bg-gray-100'
                )}
                aria-label="Previous page"
            >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Précédent</span>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
                {pageNumbers.map((page, index) =>
                    page === 'ellipsis' ? (
                        <span
                            key={`ellipsis-${index}`}
                            className="w-10 h-10 flex items-center justify-center text-gray-400"
                        >
                            <MoreHorizontal className="w-4 h-4" />
                        </span>
                    ) : (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={cn(
                                'w-10 h-10 flex items-center justify-center rounded-lg font-medium text-sm transition-all',
                                currentPage === page
                                    ? 'bg-primary-500 text-white shadow-glow'
                                    : 'text-secondary-700 hover:bg-gray-100'
                            )}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    )
                )}
            </div>

            {/* Next Button */}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={cn(
                    'flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-all',
                    currentPage === totalPages
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-secondary-700 hover:bg-gray-100'
                )}
                aria-label="Next page"
            >
                <span className="hidden sm:inline">Suivant</span>
                <ChevronRight className="w-4 h-4" />
            </button>
        </nav>
    );
}
